import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  beoordeelMenubestand,
  BESTANDSKIEZER_ACCEPT,
  BTW_HOOG,
  BTW_LAAG,
  formatteerEuro,
  isKraamCompleet,
  parseEuroNaarCenten,
  UPLOAD_UITLEG,
  type Kraamgegevens,
  type KraamgegevensVeld,
} from '@qc/core';
import { Knop } from '@qc/ui';
import { Veld } from '../auth/Veld.js';
import { useMenuInsturen, type MenuInsturen } from './useMenuInsturen.js';

interface KraamgegevensblokProps {
  code: string;
  gegevens: Kraamgegevens;
  bezig: boolean;
  bewaar: MenuInsturen['bewaarGegevens'];
}

/**
 * IBAN, KvK, BTW-nummer en contact — door de kraam zelf, op het scherm met
 * zijn code. Zonder deze gegevens valt hij na het evenement uit het
 * SEPA-bestand en kan er geen inkoopfactuur op zijn naam.
 */
function Kraamgegevensblok({ code, gegevens, bezig, bewaar }: KraamgegevensblokProps) {
  const [invoer, setInvoer] = useState<Kraamgegevens>(gegevens);
  const [fouten, setFouten] = useState<Partial<Record<KraamgegevensVeld, string>>>({});
  const [melding, setMelding] = useState<string | null>(null);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const compleet = isKraamCompleet(gegevens);

  function wijzig(veld: KraamgegevensVeld, waarde: string | boolean) {
    setInvoer((huidig) => ({ ...huidig, [veld]: waarde }) as Kraamgegevens);
    setOpgeslagen(false);
  }

  async function opslaan(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setMelding(null);
    const uitslag = await bewaar(code, invoer);
    setFouten(uitslag.fouten);
    setMelding(uitslag.melding);
    setOpgeslagen(uitslag.ok);
  }

  return (
    <section
      className={`mb-8 rounded-tegel border bg-vlak p-6 ${compleet ? 'border-goed' : 'border-rand'}`}
    >
      <h2 className="mb-1 text-xl font-bold">Jouw gegevens voor factuur en uitbetaling</h2>
      <p className={`mb-4 ${compleet ? 'text-goed' : 'text-tekst-zacht'}`}>
        {compleet
          ? `Compleet. Je uitbetaling gaat naar ${gegevens.iban}.`
          : 'Zonder IBAN, KvK- en BTW-nummer kunnen we je na het evenement niet uitbetalen. Vul het hier zelf in, dan weten we zeker dat het klopt.'}
      </p>

      <form onSubmit={(e) => void opslaan(e)} className="grid gap-4 sm:grid-cols-2">
        <Veld
          label="IBAN"
          required
          autoCapitalize="characters"
          value={invoer.iban}
          onChange={(e) => wijzig('iban', e.target.value)}
          fout={fouten.iban !== undefined}
          toelichting={fouten.iban ?? 'Het rekeningnummer waar de uitbetaling heen moet.'}
        />
        <Veld
          label="KvK-nummer"
          required
          inputMode="numeric"
          value={invoer.kvkNummer}
          onChange={(e) => wijzig('kvkNummer', e.target.value)}
          fout={fouten.kvkNummer !== undefined}
          toelichting={fouten.kvkNummer ?? 'Acht cijfers.'}
        />
        <Veld
          label="BTW-nummer"
          required
          autoCapitalize="characters"
          value={invoer.btwNummer}
          onChange={(e) => wijzig('btwNummer', e.target.value)}
          fout={fouten.btwNummer !== undefined}
          toelichting={fouten.btwNummer ?? 'Bijvoorbeeld NL123456789B01.'}
        />
        <Veld
          label="Contactpersoon"
          required
          autoComplete="name"
          value={invoer.contactpersoon}
          onChange={(e) => wijzig('contactpersoon', e.target.value)}
          fout={fouten.contactpersoon !== undefined}
          toelichting={fouten.contactpersoon ?? null}
        />
        <Veld
          label="Telefoon"
          required
          type="tel"
          autoComplete="tel"
          value={invoer.telefoon}
          onChange={(e) => wijzig('telefoon', e.target.value)}
          fout={fouten.telefoon !== undefined}
          toelichting={fouten.telefoon ?? 'Waarop we je tijdens het evenement bereiken.'}
        />
        <Veld
          label="E-mail voor de factuur"
          required
          type="email"
          autoComplete="email"
          value={invoer.email}
          onChange={(e) => wijzig('email', e.target.value)}
          fout={fouten.email !== undefined}
          toelichting={fouten.email ?? null}
        />

        <label className="flex items-start gap-3 sm:col-span-2">
          <input
            type="checkbox"
            className="mt-1 h-6 w-6 shrink-0"
            checked={invoer.selfbillingAkkoord}
            onChange={(e) => wijzig('selfbillingAkkoord', e.target.checked)}
          />
          <span className="text-lg">
            Ik ga akkoord dat Quality Cashless mijn inkoopfacturen opmaakt (selfbilling). Quality
            Cashless is de verkoper richting de klant; ik lever aan Quality Cashless.
            {fouten.selfbillingAkkoord !== undefined && (
              <span className="mt-1 block text-base text-fout">{fouten.selfbillingAkkoord}</span>
            )}
          </span>
        </label>

        {melding !== null && <p className="text-lg text-fout sm:col-span-2">{melding}</p>}
        {opgeslagen && <p className="text-lg text-goed sm:col-span-2">Opgeslagen, dank je.</p>}

        <Knop
          nadruk={compleet ? 'gewoon' : 'hoofd'}
          type="submit"
          disabled={bezig}
          className="sm:col-span-2"
        >
          {bezig ? 'Bezig…' : 'Gegevens opslaan'}
        </Knop>
      </form>
    </section>
  );
}

/**
 * Het scherm waarmee een kraam zijn menukaart instuurt.
 *
 * De kraam heeft geen account; hij typt de code van het kaartje in. Daarna kan
 * hij zijn kaart uploaden of de producten intypen, en gaat het menu naar de
 * organisator ter goedkeuring.
 *
 * Een geüploade kaart wordt uitgelezen, maar dat is een voorstel en geen
 * uitkomst: elke regel die niet zeker is blijft rood tot de kraam hem
 * bevestigt. Lukt het inlezen niet, dan typt de kraam de regels er gewoon bij
 * en gaat de rest van de keten door zoals hij is.
 */
export function MenuInsturenScherm() {
  const insturen = useMenuInsturen();
  const [code, setCode] = useState('');
  const [bestandsmelding, setBestandsmelding] = useState<string | null>(null);
  const [bestandsnaam, setBestandsnaam] = useState<string | null>(null);

  async function zoek(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    if (await insturen.zoekKraam(code)) insturen.voegRegelToe();
  }

  async function kiesBestand(gebeurtenis: ChangeEvent<HTMLInputElement>) {
    const bestand = gebeurtenis.target.files?.[0];
    if (bestand === undefined) return;

    const oordeel = beoordeelMenubestand({
      naam: bestand.name,
      type: bestand.type,
      grootte: bestand.size,
    });

    if (!oordeel.bruikbaar) {
      setBestandsnaam(null);
      setBestandsmelding(oordeel.melding);
      gebeurtenis.target.value = '';
      return;
    }

    setBestandsnaam(bestand.name);
    setBestandsmelding(null);
    setBestandsmelding(await insturen.leesKaart(code, bestand));
  }

  if (insturen.ingestuurd !== null) {
    return (
      <div className="mx-auto flex min-h-full max-w-2xl flex-col justify-center gap-4 p-8">
        <h1 className="text-3xl font-bold">Bedankt, je kaart is binnen</h1>
        <p className="text-xl text-tekst-zacht">
          {insturen.ingestuurd.aantal} {insturen.ingestuurd.aantal === 1 ? 'product' : 'producten'},
          versie {insturen.ingestuurd.versie}. De organisator kijkt hem na en keurt hem goed. Daarna
          staat hij op je kassa.
        </p>
        <p className="text-tekst-zacht">
          Klopt er iets niet? Stuur hem gewoon opnieuw in — je oude kaart blijft staan tot de nieuwe
          is goedgekeurd.
        </p>
      </div>
    );
  }

  if (insturen.kraam === null) {
    return (
      <div className="mx-auto flex min-h-full max-w-lg flex-col justify-center gap-6 p-8">
        <header>
          <p className="text-tekst-zacht">Quality Cashless</p>
          <h1 className="text-3xl font-bold">Je menukaart insturen</h1>
        </header>

        <form onSubmit={(e) => void zoek(e)} className="flex flex-col gap-4">
          <Veld
            label="Kraamcode"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            toelichting="Die staat op het kaartje in je doos, bijvoorbeeld BARDED-9X2K."
            autoCapitalize="characters"
          />
          {insturen.fout !== null && <p className="text-lg text-fout">{insturen.fout}</p>}
          <Knop nadruk="hoofd" type="submit" disabled={insturen.bezig} className="w-full">
            {insturen.bezig ? 'Bezig…' : 'Verder'}
          </Knop>
        </form>
      </div>
    );
  }

  const kraam = insturen.kraam;

  return (
    <div className="mx-auto min-h-full max-w-3xl p-6">
      <header className="mb-6">
        <p className="text-tekst-zacht">{kraam.evenementnaam}</p>
        <h1 className="text-3xl font-bold">{kraam.kraamnaam}</h1>
      </header>

      <Kraamgegevensblok
        code={code}
        gegevens={kraam.gegevens}
        bezig={insturen.gegevensBezig}
        bewaar={insturen.bewaarGegevens}
      />

      <section className="mb-8 rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-1 text-xl font-bold">Je kaart uploaden</h2>
        <p className="mb-4 text-tekst-zacht">{UPLOAD_UITLEG}</p>

        <input
          type="file"
          accept={BESTANDSKIEZER_ACCEPT}
          disabled={insturen.leest}
          onChange={(e) => void kiesBestand(e)}
          className="block w-full text-lg text-tekst-zacht file:mr-4 file:min-h-14 file:rounded-tegel file:border-0 file:bg-vlak-hoog file:px-5 file:text-lg file:text-tekst"
        />

        {bestandsnaam !== null && <p className="mt-3 text-goed">{bestandsnaam}</p>}
        {insturen.leest && (
          <p className="mt-2 text-lg text-tekst-zacht">
            Bezig met lezen — een korte kaart duurt een halve minuut, een grote kaart een paar
            minuten. Laat deze pagina open; je kunt hieronder alvast typen.
          </p>
        )}
        {bestandsmelding !== null && (
          <p
            className={`mt-2 text-lg ${bestandsnaam === null ? 'text-waarschuwing' : 'text-tekst-zacht'}`}
          >
            {bestandsmelding}
          </p>
        )}
      </section>

      <section>
        <h2 className="mb-1 text-xl font-bold">Je producten</h2>
        <p className="mb-4 text-tekst-zacht">
          Vul per product de naam, de prijs en het BTW-tarief in. Eten en fris is meestal 9%,
          alcohol 21%. Je bent zelf verantwoordelijk voor het juiste tarief.
        </p>

        <ul className="grid gap-3">
          {insturen.concept.regels.map((regel, index) => (
            <li key={index} className="rounded-tegel border border-rand bg-vlak p-4">
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
                <Veld
                  label="Product"
                  value={regel.naam}
                  onChange={(e) => insturen.wijzigRegel(index, { naam: e.target.value })}
                  fout={regel.onzeker.includes('naam')}
                />
                <Veld
                  label="Prijs"
                  inputMode="decimal"
                  value={
                    regel.prijsCent === null
                      ? ''
                      : formatteerEuro(regel.prijsCent, { metSymbool: false })
                  }
                  fout={regel.onzeker.includes('prijs')}
                  onChange={(e) => {
                    const tekst = e.target.value;
                    if (tekst.trim() === '') {
                      insturen.wijzigRegel(index, { prijsCent: null });
                      return;
                    }
                    try {
                      insturen.wijzigRegel(index, { prijsCent: parseEuroNaarCenten(tekst) });
                    } catch {
                      insturen.wijzigRegel(index, { prijsCent: null });
                    }
                  }}
                />
                <label className="block">
                  <span className="mb-1 block text-base text-tekst-zacht">BTW</span>
                  <select
                    value={regel.btwTarief ?? ''}
                    onChange={(e) =>
                      insturen.wijzigRegel(index, {
                        btwTarief:
                          e.target.value === '' ? null : (Number(e.target.value) as 900 | 2100),
                      })
                    }
                    className={`min-h-14 w-full rounded-tegel border bg-vlak px-4 text-lg text-tekst ${
                      regel.onzeker.includes('btw') ? 'border-fout' : 'border-rand'
                    }`}
                  >
                    <option value="">Kies…</option>
                    <option value={BTW_LAAG}>9% — eten en fris</option>
                    <option value={BTW_HOOG}>21% — alcohol</option>
                  </select>
                </label>
              </div>

              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Veld
                  label="Categorie"
                  value={regel.categorie}
                  onChange={(e) => insturen.wijzigRegel(index, { categorie: e.target.value })}
                  className="max-w-56"
                />
                {kraam.bekerborgCent > 0 && (
                  <label className="flex min-h-14 items-center gap-2 text-lg">
                    <input
                      type="checkbox"
                      checked={regel.bekers > 0}
                      onChange={(e) =>
                        insturen.wijzigRegel(index, { bekers: e.target.checked ? 1 : 0 })
                      }
                      className="h-6 w-6"
                    />
                    Met beker ({formatteerEuro(kraam.bekerborgCent)} borg)
                  </label>
                )}
                <Knop
                  nadruk="stil"
                  onClick={() => insturen.verwijderRegel(index)}
                  className="ml-auto px-4 text-base"
                >
                  Weghalen
                </Knop>
              </div>
            </li>
          ))}
        </ul>

        <Knop nadruk="gewoon" onClick={insturen.voegRegelToe} className="mt-4 w-full">
          Product erbij
        </Knop>
      </section>

      <div className="mt-8 rounded-tegel border border-rand bg-vlak p-6">
        {insturen.nogNaTeKijken.length > 0 && (
          <p className="mb-3 text-waarschuwing">
            {insturen.nogNaTeKijken.length === 1
              ? 'Eén regel is nog niet compleet. Vul de rode velden in.'
              : `${insturen.nogNaTeKijken.length} regels zijn nog niet compleet. Vul de rode velden in.`}
          </p>
        )}
        {insturen.fout !== null && <p className="mb-3 text-lg text-fout">{insturen.fout}</p>}

        <Knop
          nadruk="hoofd"
          className="w-full"
          disabled={!insturen.magInsturen || insturen.bezig}
          onClick={() => void insturen.stuurIn(code)}
        >
          {insturen.bezig ? 'Bezig…' : 'Insturen ter goedkeuring'}
        </Knop>
      </div>
    </div>
  );
}
